import { useEffect, useRef, useState, Fragment } from "react";
import React from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Upload, Receipt, Download, Loader2, Sparkles, Trash2, AlertTriangle, Clock, Truck, FileText as FileIcon, Paperclip, Mail, CheckCircle2, Pencil, Calculator, FileSpreadsheet, X, Search, CircleDashed, XCircle, ChevronDown, Camera, Keyboard, Wallet, Landmark } from "lucide-react";
import { PagamentoFattura } from "@/components/PagamentoFattura";
import { CreditiRitenute } from "@/components/CreditiRitenute";
import { DdtPagamentoFields, PAG_DDT_EMPTY, pagDdtCampi, pagDdtDaOcr, registraPagamentoDdt } from "@/components/DdtPagamentoFields";
import { ImportDoppioniDialog } from "@/components/ImportDoppioniDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { compressImage } from "@/lib/image";
import AnalisiIva from "@/components/AnalisiIva";
import { BulkMezzoDialog } from "@/components/BulkMezzoDialog";
import { PagatoPerMetodo } from "@/components/PagatoPerMetodo";
import { postWithDupCheck } from "@/lib/dupCheck";
import { afterAiFattura, OcrBadges } from "@/components/PagamentoMetodoDialog";
import { reviewMagazzinoItems } from "@/components/MagazzinoReview";
import { DdtRiferitiDialog } from "@/components/DdtRiferitiDialog";
import { FatturaDdtDialog } from "@/components/FatturaDdtDialog";
import { useSearchParams } from "react-router-dom";

function giorniRimanenti(dataScad) {
  if (!dataScad) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(dataScad + "T00:00:00");
  return Math.round((d - today) / 86400000);
}

function ScadenzaBadge({ stato, dataScad, tipo, onSollecita, invBusy }) {
  if (stato === "pagata") return null;
  const g = giorniRimanenti(dataScad);
  if (g === null) return null;
  const isVendita = tipo === "vendita";
  // Vendita scaduta → pulsante interattivo che invia il sollecito (sostituisce la dicitura statica)
  if (g < 0 && isVendita) {
    return (
      <Button
        size="sm"
        data-testid={`sollecita-incasso-btn`}
        onClick={onSollecita}
        disabled={invBusy}
        className="h-7 px-2 bg-destructive hover:bg-destructive/90 text-destructive-foreground font-semibold text-xs"
        title={`Credito scaduto da ${-g} giorn${-g === 1 ? "o" : "i"} · clicca per inviare l'email di sollecito al cliente`}
      >
        {invBusy
          ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          : <Mail className="h-3 w-3 mr-1" />}
        Sollecita incasso
      </Button>
    );
  }
  if (g < 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-bold text-destructive">
        <AlertTriangle className="h-3 w-3" />
        Pagamento scaduto {-g}gg
      </span>
    );
  }
  if (g === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-bold text-destructive">
        <AlertTriangle className="h-3 w-3" />
        {isVendita ? "Incasso in giornata" : "Scade oggi"}
      </span>
    );
  }
  if (g <= 7) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-600">
        <Clock className="h-3 w-3" />
        {isVendita ? `Incasso in ${g}gg` : `Scade in ${g}gg`}
      </span>
    );
  }
  if (g <= 30) return <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3 w-3" />{g}gg</span>;
  return <span className="text-xs text-muted-foreground">{g}gg</span>;
}

const MEZZI_PAGAMENTO = [["bonifico", "Bonifico"], ["pos", "POS"], ["contanti", "Contanti"], ["assegno", "Assegno"], ["altro", "Altro"]];

const empty = {
  tipo: "acquisto", cantiere_id: "", controparte: "", numero: "",
  data: new Date().toISOString().slice(0, 10), imponibile: 0, iva_percentuale: 22,
  stato: "da_pagare", data_scadenza: "", note: "", photo_base64: null,
  mezzo_pagamento: "", fornitore_id: "",
  auto_create_supplier: true,
  fornitore_partita_iva: "", fornitore_indirizzo: "", fornitore_telefono: "", fornitore_email: "",
  beni_materiali: true,
  magazzino_items: null,
  allegato_base64: null, allegato_mime: null, allegato_filename: null,
};
const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

export default function Fatture() {
  const [list, setList] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [ddtDialogFid, setDdtDialogFid] = useState(null);
  const [riferiti, setRiferiti] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const fid = searchParams.get("open");
    if (fid) { setDdtDialogFid(fid); setSearchParams({}, { replace: true }); }
  }, [searchParams, setSearchParams]);
  const [tab, setTab] = useState("ultime");
  const [mainTab, setMainTab] = useState("fatture"); // "fatture" | "analisi_iva" | "crediti"
  const [payFid, setPayFid] = useState(null);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrPreview, setOcrPreview] = useState(null); // {tipo_rilevato, fattura, ddt, photo_base64}
  const [ocrPag, setOcrPag] = useState(PAG_DDT_EMPTY);
  useEffect(() => { if (ocrPreview) setOcrPag(pagDdtDaOcr(ocrPreview)); }, [ocrPreview]);
  const [ocrBatch, setOcrBatch] = useState([]); // coda di documenti OCR da confermare
  const [ddtAperti, setDdtAperti] = useState([]);
  const [ddtAlloc, setDdtAlloc] = useState({});
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(null); // id in corso di upload
  const [bulkBusy, setBulkBusy] = useState(false); // bulk-import AdE in corso
  const [bulkResult, setBulkResult] = useState(null); // {imported, created_contacts, errors[], skipped, tipo}
  const bulkRef = useRef();
  // Assign controparte modal (per fatture con "Sconosciuto - Da associare")
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignFattura, setAssignFattura] = useState(null); // oggetto fattura
  const [assignSelId, setAssignSelId] = useState("");
  const [assignFornitori, setAssignFornitori] = useState([]);
  const [assignClienti, setAssignClienti] = useState([]);
  // Multiselect fatture (bulk delete + bulk PDF report + bulk status)
  const [selectedIds, setSelectedIds] = useState([]);
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const [bulkMezzoOpen, setBulkMezzoOpen] = useState(false);
  const [bulkActionBusy, setBulkActionBusy] = useState(false);
  // Bulk status update modal
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkStatusValue, setBulkStatusValue] = useState("pagata");
  const [bulkStatusDate, setBulkStatusDate] = useState(new Date().toISOString().slice(0, 10));
  const [bulkStatusMezzo, setBulkStatusMezzo] = useState("bonifico");
  // Registra Pagamento / Acconto modal
  const [regPayOpen, setRegPayOpen] = useState(false);
  const [regPayImporto, setRegPayImporto] = useState("");
  const [regPayMetodo, setRegPayMetodo] = useState("bonifico");
  const [regPayDate, setRegPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [regPayNote, setRegPayNote] = useState("");
  const [regPayResult, setRegPayResult] = useState(null);
  // Conflict resolution dedup (dopo bulk-import con duplicati sospetti)
  const [conflictOpen, setConflictOpen] = useState(false);
  const [conflictList, setConflictList] = useState([]); // [{row_index, new_doc, existing_matches, decision:{action,target_id}}]
  const [dupImporti, setDupImporti] = useState(null); // {list, imported} doppioni acquisti numero+data
  const applyBulkStatus = async () => {
    if (selectedIds.length === 0) return;
    setBulkActionBusy(true);
    try {
      const body = { ids: selectedIds, stato: bulkStatusValue };
      if (bulkStatusValue === "pagata") {
        if (bulkStatusDate) body.data_pagamento = bulkStatusDate;
        if (bulkStatusMezzo) body.mezzo_pagamento = bulkStatusMezzo;
      }
      const { data } = await api.post("/fatture/bulk-status", body);
      toast.success(`${data.updated} fatture aggiornate a "${bulkStatusValue === "pagata" ? "Pagata" : "Da pagare"}"`);
      setBulkStatusOpen(false);
      clearSelection();
      reloadFatture();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore aggiornamento stato"); }
    finally { setBulkActionBusy(false); }
  };
  const openRegPay = () => {
    const sel = (filtered || []).filter((f) => selectedIds.includes(f.id));
    const totale = sel.reduce((a, f) => a + Math.max(0, Number(f.totale || 0) - Number(f.importo_pagato || 0)), 0);
    setRegPayImporto(totale.toFixed(2));
    setRegPayMetodo("bonifico");
    setRegPayDate(new Date().toISOString().slice(0, 10));
    const numbers = sel.map((f) => f.numero || `#${f.id.slice(-4)}`).slice(0, 5).join(", ");
    setRegPayNote(`Acconto per fatture ${numbers}${sel.length > 5 ? "…" : ""}`);
    setRegPayResult(null);
    setRegPayOpen(true);
  };
  const applyRegPay = async () => {
    const imp = parseFloat((regPayImporto || "0").toString().replace(",", "."));
    if (!imp || imp <= 0) return toast.error("Importo versato non valido");
    setBulkActionBusy(true);
    try {
      const { data } = await api.post("/fatture/register-payment", {
        ids: selectedIds,
        importo_versato: imp,
        mezzo_pagamento: regPayMetodo,
        data_pagamento: regPayDate,
        note: regPayNote,
      });
      setRegPayResult(data);
      toast.success(`Pagamento registrato: ${data.pagate} pagate · ${data.parziali} parziali${data.eccedenza > 0 ? ` · eccedenza ${fmt(data.eccedenza)} scartata` : ""}`);
      reloadFatture();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore registrazione pagamento"); }
    finally { setBulkActionBusy(false); }
  };
  const applyConflictResolutions = async () => {
    // Costruisci risoluzioni (default = skip se non deciso)
    const resolutions = conflictList.map((c) => {
      const d = c.decision || { action: "skip" };
      return {
        action: d.action || "skip",
        target_id: d.target_id || null,
        new_doc: c.new_doc,
      };
    });
    setBulkActionBusy(true);
    try {
      const { data } = await api.post("/fatture/bulk-import/resolve", { resolutions });
      toast.success(`Conflitti risolti: ${data.inserted} inserite · ${data.overwritten} sovrascritte · ${data.skipped} saltate`);
      setConflictOpen(false);
      setConflictList([]);
      reloadFatture();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore risoluzione conflitti"); }
    finally { setBulkActionBusy(false); }
  };
  const setConflictDecision = (idx, action, target_id) => {
    setConflictList((lst) => lst.map((c, i) => i === idx ? { ...c, decision: { action, target_id: target_id || null } } : c));
  };
  const toggleSelect = (id) => setSelectedIds((s) => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleSelectAll = () => setSelectedIds((s) => s.length === filtered.length ? [] : filtered.map(f => f.id));
  const clearSelection = () => setSelectedIds([]);
  const bulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Eliminare ${selectedIds.length} fatture? L'operazione è irreversibile.`)) return;
    setBulkActionBusy(true);
    try {
      const { data } = await api.post("/fatture/bulk-delete", { ids: selectedIds });
      toast.success(`${data.deleted} fatture eliminate`);
      clearSelection();
      reloadFatture();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore eliminazione"); }
    finally { setBulkActionBusy(false); }
  };
  const bulkExportXlsx = async () => {
    const ordered = filtered.filter((f) => selectedIds.includes(f.id)).map((f) => f.id);
    if (ordered.length === 0) return;
    setExportingXlsx(true);
    try {
      const resp = await api.post("/fatture/bulk-export-xlsx", { ids: ordered }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `fatture-${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Esportate ${ordered.length} fatture`);
    } catch (e) { toast.error("Errore esportazione Excel"); }
    finally { setExportingXlsx(false); }
  };
  const bulkReportPdf = async () => {
    if (selectedIds.length === 0) return;
    setBulkActionBusy(true);
    try {
      const resp = await api.post("/fatture/bulk-report-pdf", { ids: selectedIds }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `resoconto-fatture-${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Resoconto PDF di ${selectedIds.length} fatture scaricato`);
    } catch (e) { toast.error("Errore generazione PDF"); }
    finally { setBulkActionBusy(false); }
  };
  // Filtri combinati
  const [fMese, setFMese] = useState(0); // 0=tutti
  const [fGiorno, setFGiorno] = useState("");
  const [fFornitore, setFFornitore] = useState("all");
  const [fCliente, setFCliente] = useState("all");
  const [fCantiere, setFCantiere] = useState("all");
  const attachRef = useRef();
  const pdfRef = useRef();

  // -------- Paginazione server-side per tab --------
  // Regole: "ultime" carica 10 record totali (NO load more). Le altre tab 30/richiesta con Load more.
  const PAGE_LIMIT = { ultime: 10, acquisto: 30, vendita: 30, da_pagare: 30, all: 30 };
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);
  const [sortKey, setSortKey] = useState(() => localStorage.getItem("fatture_sort") || "data_desc");
  const changeSort = (v) => { localStorage.setItem("fatture_sort", v); setSortKey(v); };
  const [fMetodo, setFMetodo] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [searchQ, setSearchQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearchQ(searchText.trim()), 400);
    return () => clearTimeout(t);
  }, [searchText]);

  const buildFattureQuery = (currentTab, currentOffset, all = false) => {
    const limit = PAGE_LIMIT[currentTab] || 30;
    const [sBy, sDir] = currentTab === "ultime" ? ["data", "desc"] : sortKey.split("_");
    const params = all ? ["load_all=true"] : [`limit=${limit}`, `offset=${currentOffset}`];
    params.push(`sort_by=${sBy}`, `sort_dir=${sDir}`);
    if (searchQ) params.push(`search=${encodeURIComponent(searchQ)}`);
    if (fMetodo !== "all") params.push(`mezzo=${fMetodo}`);
    if (currentTab === "acquisto") params.push("tipo=acquisto");
    else if (currentTab === "vendita") params.push("tipo=vendita");
    else if (currentTab === "da_pagare") params.push("stato=da_pagare");
    // "ultime" e "all": nessun filtro tipo/stato (ritorna tutte, ordinate per data desc)
    return `/fatture?${params.join("&")}`;
  };

  const loadFattureForTab = async (currentTab, append = false, all = false) => {
    const currentOffset = append ? offset : 0;
    const limit = PAGE_LIMIT[currentTab] || 30;
    if (all) setLoadingAll(true); else if (append) setLoadingMore(true); else setLoadingList(true);
    try {
      const { data } = await api.get(buildFattureQuery(currentTab, currentOffset, all));
      if (append) {
        setList((prev) => [...prev, ...(data || [])]);
      } else {
        setList(data || []);
      }
      const newOffset = currentOffset + (data?.length || 0);
      setOffset(newOffset);
      setHasMore(!all && currentTab !== "ultime" && (data?.length || 0) === limit);
    } catch (e) {
      toast.error("Errore caricamento fatture");
    } finally {
      setLoadingAll(false);
      setLoadingMore(false);
      setLoadingList(false);
    }
  };

  const load = async () => {
    // Al mount: carica SOLO anagrafiche. Le fatture sono gestite dall'effect su [tab]
    // che parte in automatico al primo render con tab="ultime" (10 record).
    const [c, fn, cl] = await Promise.all([
      api.get("/cantieri"),
      api.get("/fornitori").catch(() => ({ data: [] })),
      api.get("/clienti").catch(() => ({ data: [] })),
    ]);
    setCantieri(c.data); setFornitori(fn.data || []); setClienti(cl.data || []);
  };
  // Reload della sola lista fatture dopo mutazioni (POST/PUT/DELETE/PATCH stato).
  // Mantiene paginazione: ricarica la prima pagina della tab corrente invece di fetchare tutto.
  const reloadFatture = () => loadFattureForTab(tab, false);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  // Ogni cambio tab: ricarica da zero con il limite corretto
  useEffect(() => {
    setSelectedIds([]);
    loadFattureForTab(tab, false);
    /* eslint-disable-next-line */
  }, [tab, tab === "ultime" ? "" : sortKey, searchQ, fMetodo]);

  const [invBusy, setInvBusy] = useState(null);
  const [editingId, setEditingId] = useState(null); // id fattura in modifica

  const openEdit = (f) => {
    setEditingId(f.id);
    setForm({
      ...empty,
      tipo: f.tipo || "acquisto",
      cantiere_id: f.cantiere_id || "",
      controparte: f.controparte || "",
      numero: f.numero || "",
      data: f.data || new Date().toISOString().slice(0, 10),
      imponibile: Number(f.imponibile) || 0,
      iva_percentuale: Number(f.iva_percentuale) || 22,
      stato: f.stato || "da_pagare",
      data_scadenza: f.data_scadenza || "",
      note: f.note || "",
      mezzo_pagamento: f.mezzo_pagamento || "",
      fornitore_id: f.fornitore_id || "",
      beni_materiali: f.beni_materiali !== undefined ? !!f.beni_materiali : true,
      auto_create_supplier: false,
    });
    setOpen(true);
  };

  // Over-wrap save per gestire sia create che edit (vedi sotto)

  const inviaSollecito = async (fid) => {
    if (invBusy) return;
    setInvBusy(fid);
    try {
      const { data } = await api.post(`/fatture/${fid}/sollecito-email`);
      const sent = data?.status === "sent";
      const simulated = data?.status === "simulated";
      if (sent && data?.used_fallback) {
        toast.success(`Sollecito inviato a ${data.recipient} (cliente senza email in rubrica → fallback amministratore)`);
      } else if (sent) {
        toast.success(`Sollecito inviato a ${data.recipient}`);
      } else if (simulated) {
        // Email service non attivo: apri il client email locale dell'utente come fallback
        toast.success(`Promemoria registrato (destinatario: ${data.recipient}). Apro il client email per inviarlo manualmente.`);
        if (data.mailto_url) {
          try { window.location.href = data.mailto_url; } catch {}
        }
      } else {
        toast.success("Promemoria registrato");
      }
    } catch (e) {
      // Qualsiasi 4xx/5xx NON deve rimanere un errore rosso bloccante
      const detail = e?.response?.data?.detail || "";
      toast.success(`Promemoria registrato${detail ? " · " + detail : ""}`);
    } finally {
      setInvBusy(null);
    }
  };

  const segnaPagata = async (fid) => {
    try {
      await api.patch(`/fatture/${fid}/stato`, { stato: "pagata", data_pagamento: new Date().toISOString().slice(0, 10) });
      toast.success("Fattura segnata come pagata");
      reloadFatture();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  // Carica DDT aperti quando apri una fattura di acquisto
  useEffect(() => {
    if (open && form.tipo === "acquisto") {
      api.get("/ddt", { params: { non_fatturati: true } })
        .then(({ data }) => setDdtAperti(data))
        .catch(() => setDdtAperti([]));
    }
  }, [open, form.tipo]);

  const totDdtAlloc = Object.values(ddtAlloc).reduce((s, v) => s + (Number(v) || 0), 0);

  // Assegna manualmente un contatto (fornitore/cliente) a fattura con controparte mancante
  const openAssignControparte = async (f) => {
    setAssignFattura(f);
    setAssignSelId("");
    try {
      if (f.tipo === "acquisto") {
        const { data } = await api.get("/fornitori");
        setAssignFornitori(data || []);
      } else {
        const { data } = await api.get("/clienti");
        setAssignClienti(data || []);
      }
    } catch { /* silent */ }
    setAssignOpen(true);
  };

  const saveAssignControparte = async () => {
    if (!assignFattura || !assignSelId) return toast.error("Seleziona un contatto");
    const payload = assignFattura.tipo === "acquisto"
      ? { fornitore_id: assignSelId }
      : { cliente_id: assignSelId };
    try {
      await api.patch(`/fatture/${assignFattura.id}/controparte`, payload);
      toast.success("Contatto assegnato");
      setAssignOpen(false);
      setAssignFattura(null);
      setAssignSelId("");
      reloadFatture();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'assegnazione");
    }
  };

  // Bulk import AdE/Aruba (Excel/CSV) con Smart Dedup Heuristic
  // Default: on_duplicate="conflict" → se duplicati sospetti, apre modale di risoluzione.
  const onBulkImport = async (files, bulkTipo) => {
    if (!files || !files[0]) return;
    const file = files[0];
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo", bulkTipo);
      fd.append("on_duplicate", "conflict");
      const { data } = await api.post("/fatture/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkResult(data);
      const nConf = (data.conflicts || []).length;
      const imp = (data.conflicts || []).filter((c) => c.kind === "importi");
      const altri = (data.conflicts || []).filter((c) => c.kind !== "importi");
      if (data.imported > 0) {
        toast.success(`${data.imported} nuove fatture · ${data.created_contacts} nuovi contatti${nConf ? ` · ${nConf} doppioni da verificare` : ""}`);
      } else if (nConf === 0) {
        toast.error("Nessuna fattura importata. Controlla il formato del file.");
      }
      if (imp.length > 0) setDupImporti({ list: imp, imported: data.imported });
      if (altri.length > 0) {
        // Pre-seleziona azione di default: skip (sicura)
        setConflictList(altri.map((c) => ({ ...c, decision: { action: "skip", target_id: null } })));
        setConflictOpen(true);
      }
      reloadFatture();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'import massivo");
    } finally {
      setBulkBusy(false);
      if (bulkRef.current) bulkRef.current.value = "";
    }
  };

  const save = async () => {
    try {
      const allocs = Object.entries(ddtAlloc)
        .filter(([_id, v]) => Number(v) > 0)
        .map(([ddt_id, v]) => ({ ddt_id, importo_assegnato: Number(v) }));
      const payload = {
        ...form,
        imponibile: Number(form.imponibile),
        iva_percentuale: Number(form.iva_percentuale),
        cantiere_id: form.cantiere_id || null,
        photo_base64: null,
        ddt_allocations: form.tipo === "acquisto" && allocs.length > 0 ? allocs : null,
      };
      if (editingId) {
        // Modifica fattura esistente — PUT (il backend ignora i campi _magazzino_items / allegato per update)
        await api.put(`/fatture/${editingId}`, payload);
        toast.success("Fattura aggiornata");
      } else {
        let magItems = payload.magazzino_items;
        if (form.tipo === "acquisto" && form.beni_materiali && magItems?.length) {
          magItems = await reviewMagazzinoItems(magItems);
          if (magItems === null) return;
        }
        const r = await postWithDupCheck("/fatture", { ...payload, magazzino_items: magItems });
        if (!r) return;
        const fatt = r.data;
        if (fatt?.overwritten) {
          toast.success("Fattura esistente sovrascritta");
        } else if (fatt?.fornitore_creato?.nome) {
          toast.success(`Fattura salvata · Nuovo fornitore "${fatt.fornitore_creato.nome}" aggiunto alla rubrica`);
        } else if (fatt?.magazzino_caricato) {
          const r = fatt.magazzino_result || {};
          toast.success(`Fattura salvata · Magazzino aggiornato (${(r.inseriti || 0) + (r.aggiornati || 0)} articoli)`);
        } else if (fatt?.magazzino_skip_reason === "in_attesa_ddt") {
          toast.success("Fattura riepilogativa salvata · Il magazzino si aggiorna dopo la conferma dei DDT");
        } else if (fatt?.magazzino_skip_reason === "fattura_differita_con_ddt") {
          toast.success("Fattura differita salvata · Nessun ricarico magazzino (già fatto dai DDT)");
        } else if (fatt?.magazzino_skip_reason === "servizi_utenze_noleggi") {
          toast.success("Fattura di servizi salvata · Solo contabilità (magazzino non toccato)");
        } else {
          toast.success("Fattura salvata");
        }
        await afterAiFattura(fatt, form);
        if (form.ddt_riferiti?.length) setRiferiti({ fattura: fatt, items: form.ddt_riferiti });
      }
      setForm(empty); setDdtAlloc({}); setPreviewPhoto(null); setOpen(false); setEditingId(null); reloadFatture();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare la fattura? I DDT associati verranno scollegati.")) return;
    try { await api.delete(`/fatture/${id}`); toast.success("Eliminata"); reloadFatture(); } catch { toast.error("Errore"); }
  };

  const uploadPdfAllegato = async (fid, file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name?.toLowerCase().endsWith(".pdf")) {
      toast.error("Carica un file PDF");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("PDF troppo grande (max 20 MB)");
      return;
    }
    setPdfBusy(fid);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/fatture/${fid}/pdf-allegato`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      toast.success("PDF allegato caricato");
      reloadFatture();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fallito");
    } finally {
      setPdfBusy(null);
      if (pdfRef.current) pdfRef.current.value = "";
    }
  };

  const removePdfAllegato = async (fid) => {
    if (!window.confirm("Rimuovere il PDF allegato?")) return;
    try { await api.delete(`/fatture/${fid}/pdf-allegato`); toast.success("Allegato rimosso"); reloadFatture(); }
    catch { toast.error("Errore"); }
  };

  // Upload singolo o multiplo (immagini + PDF). Ogni file → OCR AI → pre-fill form.
  const onFiles = async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    setOcrBusy(true);
    const queue = [];
    for (const f of list) {
      try {
        const isPdf = f.type === "application/pdf" || /\.pdf$/i.test(f.name || "");
        const payload = isPdf ? f : await compressImage(f, { maxDim: 1600, quality: 0.85 });
        toast.info(`Analisi AI: ${f.name}...`);
        const fd = new FormData();
        fd.append("file", payload, f.name);
        const { data } = await api.post("/documenti/ocr", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
        });
        queue.push(data);
      } catch (e) {
        toast.error(`${f.name}: ${e?.response?.data?.detail || "OCR fallito"}`);
      }
    }
    setOcrBusy(false);
    if (attachRef.current) attachRef.current.value = "";
    if (queue.length === 0) return;
    // Mostra il primo nel modale di conferma; gli altri in coda
    setOcrBatch(queue.slice(1));
    setOcrPreview(queue[0]);
  };

  const confirmOcrType = (tipo) => {
    const current = ocrPreview;
    if (!current) return;
    if (tipo === "fattura") {
      const o = current.fattura || {};
      // Vision AI: se ha rilevato prova visiva di pagamento, pre-seleziona "pagata"
      const aiStato = (o.stato_pagamento || current.stato_pagamento || "").toLowerCase();
      const stato = aiStato === "pagata" ? "pagata" : (aiStato === "non_pagata" ? "da_pagare" : "da_pagare");
      const prova = o.prova_pagamento || current.prova_pagamento || "";
      setPreviewPhoto(current.photo_base64 || null);
      setForm((f) => ({
        ...f,
        tipo: "acquisto",
        controparte: o.fornitore || "",
        numero: o.numero || "",
        data: /^\d{4}-\d{2}-\d{2}$/.test(o.data || "") ? o.data : f.data,
        imponibile: Number(o.imponibile) || 0,
        iva_percentuale: Number(o.iva_percentuale) || 22,
        cantiere_id: o.cantiere_id || f.cantiere_id,
        stato: aiStato === "pagata" ? "pagata" : f.stato,
        data_pagamento: aiStato === "pagata" ? (/^\d{4}-\d{2}-\d{2}$/.test(o.data || "") ? o.data : new Date().toISOString().slice(0, 10)) : f.data_pagamento,
        ai_pagato: aiStato === "pagata",
        ai_metodo: o.metodo_pagamento || "",
        note: (o.descrizione || "").slice(0, 500),
        photo_base64: null,
        fornitore_id: o.fornitore_id || "",
        fornitore_partita_iva: o.fornitore_partita_iva || "",
        fornitore_indirizzo: o.fornitore_indirizzo || "",
        fornitore_telefono: o.fornitore_telefono || "",
        fornitore_email: o.fornitore_email || "",
        fornitore_extra: o.fornitore_anagrafica || null,
        auto_create_supplier: true,
        magazzino_items: Array.isArray(o.magazzino_items) && o.magazzino_items.length > 0 ? o.magazzino_items : null,
        ddt_riferiti: Array.isArray(o.ddt_riferiti) ? o.ddt_riferiti : [],
        allegato_base64: current.original_base64 || null,
        allegato_mime: current.original_mime || null,
        allegato_filename: current.original_filename || null,
      }));
      setOpen(true);
      if (aiStato === "pagata" && prova) {
        toast.success(`AI: fattura già PAGATA ✓ (${prova.slice(0, 60)}). Controlla e salva.`, { duration: 7000 });
      } else {
        toast.success("Dati estratti. Controlla e salva.");
      }
    } else {
      // DDT riconosciuto: salva direttamente, propaga anche info fornitore per auto-create backend
      const d = current.ddt || {};
      const pagD = ocrPag;
      (async () => {
        const magItems = Array.isArray(d.magazzino_items) && d.magazzino_items.length > 0 ? await reviewMagazzinoItems(d.magazzino_items) : null;
        if (magItems === null && d.magazzino_items?.length) return null;
        return postWithDupCheck("/ddt", {
        fornitore_id: d.fornitore_id || null,
        cantiere_id: d.cantiere_id || null,
        numero: d.numero || "",
        data: /^\d{4}-\d{2}-\d{2}$/.test(d.data || "") ? d.data : new Date().toISOString().slice(0, 10),
        descrizione: d.descrizione || "",
        importo_stimato: Number(d.importo_stimato) || 0,
        ...pagDdtCampi(pagD),
        magazzino_items: magItems,
        allegato_base64: current.original_base64 || null,
        allegato_mime: current.original_mime || null,
        allegato_filename: current.original_filename || null,
        // Dati per auto-resolve/create fornitore dal backend
        auto_create_supplier: true,
        fornitore_nome: d.fornitore || "",
        fornitore_partita_iva: d.fornitore_partita_iva || "",
        fornitore_indirizzo: d.fornitore_indirizzo || "",
        fornitore_telefono: d.fornitore_telefono || "",
        fornitore_email: d.fornitore_email || "",
      });
      })().then(async (r) => {
        if (!r) return;
        await registraPagamentoDdt(r?.data?.id, pagD);
        const n = r?.data?.magazzino_result;
        const extra = n ? ` · Magazzino: +${(n.inseriti||0)+(n.aggiornati||0)} articoli` : "";
        const forn = r?.data?.fornitore_creato ? ` · Nuovo fornitore "${r.data.fornitore_creato.nome}" aggiunto` : "";
        toast.success("DDT salvato" + extra + forn);
      }).catch(() => toast.error("Errore salvataggio DDT"));
    }
    setOcrPreview(null);
    // Passa al prossimo nella coda
    if (ocrBatch.length > 0) {
      const next = ocrBatch[0];
      setOcrBatch(ocrBatch.slice(1));
      setTimeout(() => setOcrPreview(next), 400);
    }
  };

  const sortCmp = (a, b) => {
    const [sBy, sDir] = sortKey.split("_");
    const d = sDir === "asc" ? 1 : -1;
    if (sBy === "nome") return d * (a.controparte || "").localeCompare(b.controparte || "", "it", { sensitivity: "base" });
    return d * (a.data || "").localeCompare(b.data || "");
  };
  const baseList = tab === "ultime" ? [...(list || [])].sort(sortCmp) : (list || []);
  const filtered = baseList.filter((f) => {
    // Client-side filters applicano solo alla pagina caricata; il filtro tipo/stato è già server-side.
    if (fMese) { const m = parseInt((f.data || "").slice(5, 7), 10); if (m !== fMese) return false; }
    if (fGiorno && !(f.data || "").startsWith(fGiorno)) return false;
    if (fCantiere !== "all" && f.cantiere_id !== fCantiere) return false;
    if (fFornitore !== "all") {
      const nome = (fornitori.find((x) => x.id === fFornitore) || {}).nome || "";
      if (f.fornitore_id !== fFornitore && (f.controparte || "").toLowerCase() !== nome.toLowerCase()) return false;
    }
    if (fCliente !== "all") {
      const lbl = (clienti.find((x) => x.id === fCliente) || {}).label || "";
      if ((f.controparte || "").toLowerCase() !== lbl.toLowerCase()) return false;
    }
    return true;
  });

  const cmap = Object.fromEntries(cantieri.map((c) => [c.id, c.nome]));
  const kpiRows = selectedIds.length > 0 ? filtered.filter((f) => selectedIds.includes(f.id)) : filtered;
  const totImp = kpiRows.reduce((s, f) => s + (f.imponibile || 0), 0);
  const totIva = kpiRows.reduce((s, f) => s + (f.iva_importo || 0), 0);
  const totTot = kpiRows.reduce((s, f) => s + (f.totale || 0), 0);
  const rimanenza = (f) => (f.stato === "pagata" ? null
    : Math.max(0, (f.totale || 0) - (f.pagamenti || []).reduce((s, p) => s + (Number(p.importo) || 0), 0)));

  return (
    <div className="space-y-6">
      {/* Hidden file input per upload allegato PDF */}
      <input
        ref={pdfRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={(e) => {
          const fid = pdfRef.current?.dataset?.fid;
          const file = e.target.files?.[0];
          if (fid && file) uploadPdfAllegato(fid, file);
        }}
        data-testid="pdf-allegato-input"
      />
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Contabilità</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Fatture & IVA</h1>
          <p className="text-muted-foreground mt-1">Scatta una foto, l'AI estrae i dati e assegna il cantiere.</p>
        </div>
        <div className="flex gap-2" style={{ display: mainTab === "fatture" ? undefined : "none" }}>
          <input ref={attachRef} type="file" multiple accept="image/*,application/pdf" className="hidden"
                 onChange={(e) => onFiles(e.target.files)} data-testid="attach-file" />
          <input ref={bulkRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
                 onChange={(e) => {
                   const bulkTipo = bulkRef.current?.dataset?.tipo || "acquisto";
                   onBulkImport(e.target.files, bulkTipo);
                 }} data-testid="bulk-file" />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{form.photo_base64 ? "Conferma dati estratti dall'AI" : "Nuova fattura"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Tipo</Label>
                  <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                    <SelectTrigger data-testid="f-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="acquisto">Acquisto (fornitore)</SelectItem>
                      <SelectItem value="vendita">Vendita (cliente)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Controparte</Label><Input data-testid="f-controparte" value={form.controparte} onChange={(e) => setForm({ ...form, controparte: e.target.value })} /></div>
                {form.tipo === "acquisto" && (
                  <div className="col-span-2">
                    <Label>Partita IVA fornitore (opz.)</Label>
                    <Input data-testid="f-piva" placeholder="11 cifre"
                           value={form.fornitore_partita_iva || ""}
                           onChange={(e) => setForm({ ...form, fornitore_partita_iva: e.target.value })} />
                  </div>
                )}
                <div><Label>Numero</Label><Input value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} /></div>
                <div><Label>Data</Label><Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} /></div>
                <div><Label>Imponibile €</Label><Input data-testid="f-imponibile" type="number" step="0.01" value={form.imponibile} onChange={(e) => setForm({ ...form, imponibile: e.target.value })} /></div>
                <div><Label>IVA %</Label><Input type="number" step="0.1" value={form.iva_percentuale} onChange={(e) => setForm({ ...form, iva_percentuale: e.target.value })} /></div>
                <div><Label>Cantiere</Label>
                  <Select value={form.cantiere_id} onValueChange={(v) => setForm({ ...form, cantiere_id: v })}>
                    <SelectTrigger data-testid="f-cantiere"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                    <SelectContent>{cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Stato</Label>
                  <Select value={form.stato} onValueChange={(v) => setForm({ ...form, stato: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="da_pagare">Da pagare</SelectItem>
                      <SelectItem value="pagata">Pagata</SelectItem>
                      <SelectItem value="parziale">Parziale</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Scadenza</Label><Input type="date" value={form.data_scadenza} onChange={(e) => setForm({ ...form, data_scadenza: e.target.value })} /></div>
                {form.tipo === "acquisto" && (
                  <div>
                    <Label>Mezzo pagamento</Label>
                    <Select value={form.mezzo_pagamento || "__none__"} onValueChange={(v) => setForm({ ...form, mezzo_pagamento: v === "__none__" ? "" : v })}>
                      <SelectTrigger data-testid="f-mezzo"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— nessuno —</SelectItem>
                        {MEZZI_PAGAMENTO.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {form.tipo === "acquisto" && !form.fornitore_id && (form.controparte || "").trim() && (
                  <div className="col-span-2 flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
                    <input
                      id="auto-forn"
                      type="checkbox"
                      data-testid="f-auto-forn"
                      className="mt-0.5 h-4 w-4 accent-primary"
                      checked={!!form.auto_create_supplier}
                      onChange={(e) => setForm({ ...form, auto_create_supplier: e.target.checked })}
                    />
                    <label htmlFor="auto-forn" className="text-xs leading-snug cursor-pointer">
                      <span className="font-bold">Aggiungi automaticamente alla rubrica fornitori</span>
                      <span className="block text-muted-foreground">
                        Se "{form.controparte}" non è ancora in anagrafica, verrà creato (con P.IVA, indirizzo, contatti estratti dall'AI) e collegato a questa fattura.
                      </span>
                    </label>
                  </div>
                )}
                {form.tipo === "acquisto" && (
                  <div className="col-span-2 rounded-lg border bg-secondary/40 p-3 space-y-2">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                      Classificazione contabile
                    </div>
                    <div className="flex items-start gap-2">
                      <input
                        id="beni-materiali"
                        type="checkbox"
                        data-testid="f-beni-materiali"
                        className="mt-0.5 h-4 w-4 accent-primary"
                        checked={!!form.beni_materiali}
                        onChange={(e) => setForm({ ...form, beni_materiali: e.target.checked })}
                      />
                      <label htmlFor="beni-materiali" className="text-xs leading-snug cursor-pointer flex-1">
                        <span className="font-bold">Carica in magazzino: {form.beni_materiali ? "Sì" : "No"}</span>
                        <span className="block text-muted-foreground">
                          {form.beni_materiali
                            ? "Beni/Materiali — aggiorna l'inventario se non c'è un DDT già collegato (per fatture differite con DDT il carico è già avvenuto)."
                            : "Servizi/Utenze/Noleggi/Consulenze — registra SOLO il costo finanziario, non tocca il magazzino."}
                        </span>
                      </label>
                    </div>
                  </div>
                )}
                <div className="col-span-2"><Label>Note</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
                {form.tipo === "acquisto" && ddtAperti.length > 0 && (
                  <div className="col-span-2 border rounded-lg p-3 space-y-2 bg-secondary/40">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="text-sm font-bold flex items-center gap-1.5">
                        <Truck className="h-4 w-4" /> DDT da collegare a questa fattura
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        Totale allocato: <strong>{fmt(totDdtAlloc)}</strong> / Imponibile fattura: <strong>{fmt(Number(form.imponibile) || 0)}</strong>
                      </div>
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      Spunta i DDT compresi in questa fattura e scrivi quanto spetta ad ognuno (per attribuire i costi al giusto cantiere).
                    </div>
                    <div className="max-h-56 overflow-y-auto space-y-1">
                      {ddtAperti.map((d) => {
                        const checked = d.id in ddtAlloc;
                        const cantNome = (cantieri.find((c) => c.id === d.cantiere_id) || {}).nome || "—";
                        return (
                          <div key={d.id} data-testid={`ddt-row-${d.id}`} className="flex items-center gap-2 bg-card rounded px-2 py-1.5">
                            <input
                              type="checkbox"
                              data-testid={`ddt-chk-${d.id}`}
                              checked={checked}
                              onChange={(e) => {
                                setDdtAlloc((prev) => {
                                  const next = { ...prev };
                                  if (e.target.checked) next[d.id] = d.importo_stimato || 0;
                                  else delete next[d.id];
                                  return next;
                                });
                              }}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium truncate">
                                DDT {d.numero || "s.n."} · {d.data} · <span className="text-muted-foreground">{cantNome}</span>
                              </div>
                              {d.descrizione && <div className="text-[11px] text-muted-foreground truncate">{d.descrizione}</div>}
                            </div>
                            {checked && (
                              <Input
                                data-testid={`ddt-amount-${d.id}`}
                                type="number" step="0.01" min="0"
                                value={ddtAlloc[d.id]}
                                onChange={(e) => setDdtAlloc((prev) => ({ ...prev, [d.id]: e.target.value }))}
                                className="h-8 w-28 text-xs"
                                placeholder="€ voce"
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {previewPhoto && (
                  <div className="col-span-2 bg-secondary rounded-lg p-2">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1 mb-2">
                      <Sparkles className="h-3 w-3" /> Foto originale
                    </div>
                    <img src={`data:image/jpeg;base64,${previewPhoto}`} alt="fattura" className="max-h-48 rounded" />
                  </div>
                )}
              </div>
              <Button data-testid="f-save" onClick={save} className="h-11 mt-2">
                Totale: {fmt(Number(form.imponibile) * (1 + Number(form.iva_percentuale) / 100))} · Salva
              </Button>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={mainTab} onValueChange={setMainTab}>
      <PagamentoFattura fid={payFid} onClose={() => setPayFid(null)} onSaved={() => mainTab === "fatture" && reloadFatture()} />
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <TabsList>
          <TabsTrigger value="fatture" data-testid="main-tab-fatture">
            <Receipt className="h-4 w-4 mr-2" />Fatture
          </TabsTrigger>
          <TabsTrigger value="analisi_iva" data-testid="main-tab-analisi-iva">
            <Calculator className="h-4 w-4 mr-2" />Analisi IVA
          </TabsTrigger>
          <TabsTrigger value="crediti" data-testid="main-tab-crediti">
            <Landmark className="h-4 w-4 mr-2" />Crediti
          </TabsTrigger>
        </TabsList>
        {mainTab === "fatture" && (
          <div className="flex flex-wrap items-center gap-2" data-testid="fatture-header-actions">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="h-10" disabled={bulkBusy} data-testid="importa-ade-menu">
                  {bulkBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                  Importa Fatture (AdE) <ChevronDown className="h-4 w-4 ml-1 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem data-testid="importa-ade-acquisti" onSelect={() => { bulkRef.current.dataset.tipo = "acquisto"; bulkRef.current.click(); }}>
                  <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" /> Acquisti
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="importa-ade-vendite" onSelect={() => { bulkRef.current.dataset.tipo = "vendita"; bulkRef.current.click(); }}>
                  <FileSpreadsheet className="h-4 w-4 mr-2 text-sky-600" /> Vendite
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="h-10 font-bold" disabled={ocrBusy} data-testid="registra-fattura-menu">
                  {ocrBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  Registra Fattura <ChevronDown className="h-4 w-4 ml-1 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem data-testid="attach-btn" onSelect={() => attachRef.current?.click()}>
                  <Camera className="h-4 w-4 mr-2 text-primary" /> Scatta o allega con AI
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="new-fattura" onSelect={() => { setEditingId(null); setForm(empty); setPreviewPhoto(null); setOpen(true); }}>
                  <Keyboard className="h-4 w-4 mr-2" /> Inserimento manuale
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
        </div>

        <TabsContent value="analisi_iva" className="pt-4">
          <AnalisiIva />
        </TabsContent>

        <TabsContent value="crediti" className="pt-4">
          {mainTab === "crediti" && <CreditiRitenute onOpenFattura={setPayFid} />}
        </TabsContent>

        <TabsContent value="fatture" className="pt-4 space-y-6">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="ultime" data-testid="tab-ultime">Ultime</TabsTrigger>
              <TabsTrigger value="acquisto" data-testid="tab-acquisto">Acquisti</TabsTrigger>
              <TabsTrigger value="vendita" data-testid="tab-vendita">Vendite</TabsTrigger>
              <TabsTrigger value="da_pagare" data-testid="tab-dapagare">Da pagare</TabsTrigger>
              <TabsTrigger value="all" data-testid="tab-all">Tutte</TabsTrigger>
            </TabsList>
          </Tabs>

      <Card className="card-elevated">
        <CardContent className="p-3 flex items-end gap-2 flex-wrap">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mr-2 self-center">Filtri</div>
          <div>
            <Label className="text-[10px]">Mese</Label>
            <Select value={String(fMese)} onValueChange={(v) => setFMese(parseInt(v, 10))}>
              <SelectTrigger data-testid="flt-fatt-mese" className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Tutti</SelectItem>
                {["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"].map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px]">Giorno</Label>
            <Input data-testid="flt-fatt-giorno" type="date" value={fGiorno} onChange={(e) => setFGiorno(e.target.value)} className="h-8 w-36 text-xs" />
          </div>
          <div>
            <Label className="text-[10px]">Fornitore</Label>
            <Select value={fFornitore} onValueChange={setFFornitore}>
              <SelectTrigger data-testid="flt-fatt-fornitore" className="h-8 w-44 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i fornitori</SelectItem>
                {fornitori.map((f) => <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px]">Cliente</Label>
            <Select value={fCliente} onValueChange={setFCliente}>
              <SelectTrigger data-testid="flt-fatt-cliente" className="h-8 w-44 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i clienti</SelectItem>
                {clienti.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px]">Cantiere</Label>
            <Select value={fCantiere} onValueChange={setFCantiere}>
              <SelectTrigger data-testid="flt-fatt-cantiere" className="h-8 w-44 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i cantieri</SelectItem>
                {cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {(fMese || fGiorno || fFornitore !== "all" || fCliente !== "all" || fCantiere !== "all") && (
            <Button size="sm" variant="ghost" onClick={() => { setFMese(0); setFGiorno(""); setFFornitore("all"); setFCliente("all"); setFCantiere("all"); }}>Pulisci</Button>
          )}
        </CardContent>
      </Card>

      {selectedIds.length > 0 && (
        <div className="flex items-center gap-2 text-xs font-semibold text-primary" data-testid="kpi-selezione-label">
          <CheckCircle2 className="h-4 w-4" /> Selezione: {kpiRows.length} fatture · i riquadri mostrano solo le righe selezionate
        </div>
      )}
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="card-elevated"><CardContent className="p-5"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Imponibile</div><div className="text-2xl font-extrabold" data-testid="kpi-imponibile">{fmt(totImp)}</div></CardContent></Card>
        <Card className="card-elevated"><CardContent className="p-5"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">IVA</div><div className="text-2xl font-extrabold" data-testid="kpi-iva">{fmt(totIva)}</div></CardContent></Card>
        <Card className="card-elevated"><CardContent className="p-5"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Totale</div><div className="text-2xl font-extrabold text-primary" data-testid="kpi-totale">{fmt(totTot)}</div></CardContent></Card>
        <PagatoPerMetodo tab={tab} mese={fMese} giorno={fGiorno} refreshKey={list} selectedIds={selectedIds.length > 0 ? kpiRows.map((f) => f.id) : []} />
      </div>

      <div className="flex flex-wrap items-center gap-3" data-testid="fatture-sort-bar">
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Ordina per</Label>
        <Select value={sortKey} onValueChange={changeSort}>
          <SelectTrigger className="w-64 h-9" data-testid="fatture-sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="data_desc" data-testid="sort-data-desc">Data emissione · Più recenti</SelectItem>
            <SelectItem value="data_asc" data-testid="sort-data-asc">Data emissione · Meno recenti</SelectItem>
            <SelectItem value="nome_asc" data-testid="sort-nome-asc">Nome · A-Z</SelectItem>
            <SelectItem value="nome_desc" data-testid="sort-nome-desc">Nome · Z-A</SelectItem>
          </SelectContent>
        </Select>
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Metodo</Label>
        <Select value={fMetodo} onValueChange={setFMetodo}>
          <SelectTrigger className="w-44 h-9" data-testid="fatture-metodo-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="metodo-all">Tutti</SelectItem>
            <SelectItem value="bonifico" data-testid="metodo-bonifico">Bonifico</SelectItem>
            <SelectItem value="pos" data-testid="metodo-pos">POS</SelectItem>
            <SelectItem value="contanti" data-testid="metodo-contanti">Contanti</SelectItem>
            <SelectItem value="assegno" data-testid="metodo-assegno">Assegno</SelectItem>
            <SelectItem value="none" data-testid="metodo-none">Non specificato</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative w-full sm:w-72 order-first sm:order-none">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={searchText} onChange={(e) => setSearchText(e.target.value)}
                 placeholder="Cerca nome, cantiere, numero o importo" className="h-9 pl-8 pr-8"
                 data-testid="fatture-search-input" />
          {searchText && (
            <button type="button" onClick={() => setSearchText("")} data-testid="fatture-search-clear"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {loadingList && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        <span className="text-xs text-muted-foreground" data-testid="fatture-loaded-count">{filtered.length} fatture visualizzate</span>
      </div>

      <Card className="card-elevated">
        <CardContent className="p-0 overflow-x-auto">
          {selectedIds.length > 0 && (
            <div
              data-testid="bulk-action-bar"
              className="sticky top-0 z-10 flex items-center justify-between gap-3 px-4 py-2 bg-primary/10 border-b border-primary/30"
            >
              <div className="text-sm font-semibold text-primary">
                {selectedIds.length} selezionate
                <button onClick={clearSelection} className="ml-3 text-xs text-muted-foreground underline">deseleziona</button>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button data-testid="bulk-status-btn" size="sm" variant="outline"
                        onClick={openRegPay} disabled={bulkActionBusy}
                        className="h-8 border-emerald-500/50 bg-emerald-500/5 hover:bg-emerald-500/10 text-emerald-700">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Registra Pagamento / Acconto
                </Button>
                <Button data-testid="bulk-mezzo-btn" size="sm" variant="outline"
                        onClick={() => setBulkMezzoOpen(true)} disabled={bulkActionBusy}
                        className="h-8 border-amber-500/50 bg-amber-500/5 hover:bg-amber-500/10 text-amber-700">
                  <Pencil className="h-3 w-3 mr-1" />
                  Assegna metodo
                </Button>
                <Button data-testid="bulk-xlsx-btn" size="sm" variant="outline"
                        onClick={bulkExportXlsx} disabled={bulkActionBusy || exportingXlsx}
                        className="h-8 border-green-600/50 bg-green-600/5 hover:bg-green-600/10 text-green-700">
                  {exportingXlsx ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileSpreadsheet className="h-3 w-3 mr-1" />}
                  Esporta Excel
                </Button>
                <Button data-testid="bulk-pdf-btn" size="sm" variant="outline"
                        onClick={bulkReportPdf} disabled={bulkActionBusy}
                        className="h-8 border-sky-500/50 bg-sky-500/5 hover:bg-sky-500/10">
                  {bulkActionBusy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileIcon className="h-3 w-3 mr-1 text-sky-600" />}
                  Genera Resoconto PDF
                </Button>
                <Button data-testid="bulk-delete-btn" size="sm" variant="outline"
                        onClick={bulkDelete} disabled={bulkActionBusy}
                        className="h-8 border-red-500/50 bg-red-500/5 hover:bg-red-500/10 text-red-700">
                  <Trash2 className="h-3 w-3 mr-1" />
                  Elimina Selezionate
                </Button>
              </div>
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-secondary">
              <tr className="text-left">
                <th className="px-2 py-3 w-10">
                  <input type="checkbox" data-testid="sel-all"
                         checked={filtered.length > 0 && selectedIds.length === filtered.length}
                         onChange={toggleSelectAll}
                         className="h-4 w-4 cursor-pointer accent-primary" />
                </th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Data</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Tipo</th>
                <th className="px-3 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Nr.</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Controparte</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Cantiere</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Imponibile</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">IVA</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Totale</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Rimanenza</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Stato</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Scadenza</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((f) => {
                const g = giorniRimanenti(f.data_scadenza);
                const isVenditaScaduta = f.tipo === "vendita" && f.stato !== "pagata" && g !== null && g < 0;
                const isAcquistoScaduto = f.tipo === "acquisto" && f.stato !== "pagata" && g !== null && g < 0;
                return (
                <React.Fragment key={f.id}>
                <tr className={`border-t border-border ${selectedIds.includes(f.id) ? "bg-primary/5" : ""}`}>
                  <td className="px-2 py-3 w-10">
                    <input type="checkbox" data-testid={`sel-${f.id}`}
                           checked={selectedIds.includes(f.id)}
                           onChange={() => toggleSelect(f.id)}
                           className="h-4 w-4 cursor-pointer accent-primary" />
                  </td>
                  <td className="px-4 py-3">{f.data}</td>
                  <td className="px-4 py-3">
                    <Badge className={f.tipo === "vendita" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>
                      {f.tipo === "vendita" ? "Vendita" : "Acquisto"}
                    </Badge>
                  </td>
                  <td className="px-3 py-3 text-xs font-mono whitespace-nowrap" data-testid={`fatt-nr-${f.id}`}>{(f.numero || "").trim() || <span className="text-muted-foreground">ND</span>}</td>
                  <td className="px-4 py-3 font-medium">
                    {(f.controparte && f.controparte.trim()) ? (
                      f.controparte
                    ) : (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 underline underline-offset-2 font-semibold text-xs cursor-pointer"
                        onClick={() => openAssignControparte(f)}
                        data-testid={`assign-controparte-${f.id}`}
                        title="Clicca per assegnare manualmente il contatto"
                      >
                        <AlertTriangle className="h-3 w-3" />
                        Sconosciuto - Da associare
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{cmap[f.cantiere_id] || "—"}</td>
                  <td className="px-4 py-3 tabular-nums">{fmt(f.imponibile)}</td>
                  <td className="px-4 py-3 tabular-nums text-muted-foreground">{fmt(f.iva_importo)}</td>
                  <td className="px-4 py-3 tabular-nums font-bold">{fmt(f.totale)}</td>
                  <td className="px-4 py-3 tabular-nums" data-testid={`fatt-rimanenza-${f.id}`}>{rimanenza(f) === null ? <span className="text-muted-foreground">--</span> : <span className="font-semibold text-red-700">{fmt(rimanenza(f))}</span>}</td>
                  <td className="px-4 py-3">
                    <Select value={f.stato} onValueChange={async (v) => { try { await api.patch(`/fatture/${f.id}/stato`, { stato: v }); toast.success("Stato aggiornato"); reloadFatture(); } catch { toast.error("Errore"); } }}>
                      <SelectTrigger data-testid={`stato-${f.id}`} className={`h-7 w-auto gap-1 px-2 rounded-full text-[11px] font-semibold [&>svg:last-child]:hidden ${f.stato === "pagata" ? "bg-emerald-100 text-emerald-800 border-emerald-300" : f.stato === "parziale" ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-red-100 text-red-800 border-red-300"}`}>
                        {f.stato === "pagata" ? <CheckCircle2 className="h-3 w-3" /> : f.stato === "parziale" ? <CircleDashed className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="da_pagare">Da pagare</SelectItem>
                        <SelectItem value="parziale">Parziale</SelectItem>
                        <SelectItem value="pagata">Pagata</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    <ScadenzaBadge
                      stato={f.stato}
                      dataScad={f.data_scadenza}
                      tipo={f.tipo}
                      onSollecita={() => inviaSollecito(f.id)}
                      invBusy={invBusy === f.id}
                    />
                  </td>
                  <td className="px-4 py-3 flex gap-1 justify-end">
                    {(f.pdf_filename || f.allegato_filename) ? (
                      <>
                        <a
                          href={`${API}${f.allegato_filename ? `/fatture/${f.id}/allegato` : `/fatture/${f.id}/pdf-allegato`}`}
                          target="_blank" rel="noreferrer"
                          title={`Scarica ${f.allegato_filename || f.pdf_filename}`}
                        >
                          <Button variant="ghost" size="icon" data-testid={`dl-allegato-${f.id}`}>
                            <Download className="h-3 w-3 text-emerald-600" />
                          </Button>
                        </a>
                        {f.pdf_filename && !f.allegato_filename && (
                          <Button variant="ghost" size="icon" title="Rimuovi allegato" onClick={() => removePdfAllegato(f.id)}>
                            <Trash2 className="h-3 w-3 text-muted-foreground" />
                          </Button>
                        )}
                      </>
                    ) : (
                      <Button variant="ghost" size="icon" title="Carica PDF originale" disabled={pdfBusy === f.id}
                              onClick={() => { pdfRef.current.dataset.fid = f.id; pdfRef.current.click(); }}
                              data-testid={`up-allegato-${f.id}`}>
                        {pdfBusy === f.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Paperclip className="h-3 w-3" />}
                      </Button>
                    )}
                    <a href={`${API}/pdf/fattura/${f.id}`} target="_blank" rel="noreferrer" title="PDF riepilogo generato"><Button variant="ghost" size="icon"><FileIcon className="h-3 w-3" /></Button></a>
                    <Button variant="ghost" size="sm" className="h-8 px-2" title="DDT collegati" onClick={() => setDdtDialogFid(f.id)} data-testid={`fatt-ddt-${f.id}`}>
                      <Truck className="h-3 w-3" />{(f.ddt_ids || []).length > 0 && <span className="ml-1 text-[10px] font-bold">{f.ddt_ids.length}</span>}
                      {(f.ddt_ids || []).length > 0 && Math.abs((Number(f.ddt_somma) || 0) - (Number(f.imponibile) || 0)) > 1 && (
                        <AlertTriangle className="h-3 w-3 ml-1 text-red-600" data-testid={`fatt-ddt-warning-${f.id}`} />
                      )}
                    </Button>
                    <Button variant="ghost" size="icon" title="Registra pagamento" onClick={() => setPayFid(f.id)} data-testid={`pay-fatt-${f.id}`}>
                      <Wallet className="h-3 w-3 text-emerald-600" />
                    </Button>
                    <Button variant="ghost" size="icon" title="Modifica" onClick={() => openEdit(f)} data-testid={`edit-fatt-${f.id}`}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(f.id)}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                  </td>
                </tr>
                {isAcquistoScaduto && (
                  <tr className="border-t bg-amber-50/40 dark:bg-amber-900/10" data-testid={`row-azioni-${f.id}`}>
                    <td colSpan={13} className="px-4 py-2">
                      <div className="text-xs">
                        <span className="text-amber-800 dark:text-amber-200 font-semibold">
                          <AlertTriangle className="inline h-3 w-3 mr-1" />
                          Debito scaduto da {-g}gg · da pagare al fornitore
                        </span>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={13} className="px-4 py-10 text-center text-muted-foreground"><Receipt className="h-6 w-6 mx-auto mb-2 opacity-50" /> Nessuna fattura.</td></tr>}
            </tbody>
          </table>
          {/* Load more / Paginazione server-side (nascosto nella tab Ultime che è limitata a 10) */}
          {tab !== "ultime" && hasMore && (
            <div className="flex justify-center gap-2 py-3 border-t">
              <Button
                variant="outline"
                size="sm"
                disabled={loadingMore || loadingAll}
                onClick={() => loadFattureForTab(tab, true)}
                data-testid="fatture-load-more"
              >
                {loadingMore ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Carica altre 30
              </Button>
              <Button
                variant="default"
                size="sm"
                disabled={loadingMore || loadingAll}
                onClick={() => loadFattureForTab(tab, false, true)}
                data-testid="fatture-load-all"
              >
                {loadingAll ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Carica Tutte
              </Button>
            </div>
          )}
          {tab === "ultime" && (
            <div className="text-center py-2 border-t text-[10px] text-muted-foreground">
              Visualizzate le 10 fatture più recenti. Apri "Tutte" o un'altra tab per lo storico completo.
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>

      {/* Modale assegnazione manuale controparte (per fatture con "Sconosciuto - Da associare") */}
      <Dialog open={assignOpen} onOpenChange={(o) => { setAssignOpen(o); if (!o) { setAssignFattura(null); setAssignSelId(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Assegna {assignFattura?.tipo === "acquisto" ? "Fornitore" : "Cliente"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">
              Fattura <b>N° {assignFattura?.numero || "—"}</b> del <b>{assignFattura?.data || "—"}</b> · Totale <b>{fmt(assignFattura?.totale || 0)}</b>
            </div>
            <div>
              <Label>Seleziona dalla rubrica</Label>
              <Select value={assignSelId} onValueChange={setAssignSelId}>
                <SelectTrigger data-testid="assign-select"><SelectValue placeholder={`— scegli ${assignFattura?.tipo === "acquisto" ? "fornitore" : "cliente"} —`} /></SelectTrigger>
                <SelectContent>
                  {(assignFattura?.tipo === "acquisto" ? assignFornitori : assignClienti).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.nome || c.ragione_sociale || `${c.nome || ""} ${c.cognome || ""}`.trim() || c.partita_iva || "—"}
                      {c.partita_iva ? ` · ${c.partita_iva}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="assign-save" onClick={saveAssignControparte} disabled={!assignSelId} className="w-full h-11">
              Associa alla fattura
            </Button>
            <div className="text-[11px] text-muted-foreground">
              Il nome del contatto apparirà come "Controparte" nella tabella e sarà usato per i prossimi match automatici.
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modale Registra Pagamento / Acconto (FIFO distribution) */}
      <FatturaDdtDialog fid={ddtDialogFid} onClose={() => setDdtDialogFid(null)} onChanged={reloadFatture} />
      <DdtRiferitiDialog fattura={riferiti?.fattura} items={riferiti?.items} onClose={() => setRiferiti(null)} onDone={reloadFatture} />
      <BulkMezzoDialog open={bulkMezzoOpen} onOpenChange={setBulkMezzoOpen}
        selected={(list || []).filter((f) => selectedIds.includes(f.id))}
        onDone={() => { clearSelection(); reloadFatture(); }}
        onOpenInvoice={(f) => { setBulkMezzoOpen(false); openEdit(f); }} />
      <Dialog open={regPayOpen} onOpenChange={(o) => { setRegPayOpen(o); if (!o) { setRegPayResult(null); clearSelection(); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              Registra Pagamento / Acconto ({selectedIds.length} fatture)
            </DialogTitle>
            <DialogDescription>L'importo verrà distribuito automaticamente in ordine FIFO (per data fattura crescente). L'eccedenza viene scartata.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* Riepilogo fatture selezionate */}
            <div className="bg-secondary/50 rounded-lg p-3 border">
              <div className="text-xs font-semibold text-muted-foreground mb-2">Fatture selezionate (ordinate per data)</div>
              <div className="max-h-32 overflow-y-auto">
                {[...filtered.filter((f) => selectedIds.includes(f.id))].sort((a, b) => (a.data || "").localeCompare(b.data || "")).map((f) => {
                  const residuo = Math.max(0, Number(f.totale || 0) - Number(f.importo_pagato || 0));
                  return (
                    <div key={f.id} className="flex justify-between items-center text-xs py-1 border-b border-border/40 last:border-0">
                      <div className="truncate flex-1">
                        <b>{f.numero || `#${f.id.slice(-4)}`}</b> · {f.data} · {(f.controparte || "—").slice(0, 32)}
                      </div>
                      <div className="tabular-nums font-semibold text-primary ml-2">{fmt(residuo)}</div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between items-center pt-2 mt-1 border-t text-sm">
                <span className="font-semibold">Totale dovuto</span>
                <span className="tabular-nums font-extrabold text-lg">
                  {fmt(filtered.filter((f) => selectedIds.includes(f.id)).reduce((a, f) => a + Math.max(0, Number(f.totale || 0) - Number(f.importo_pagato || 0)), 0))}
                </span>
              </div>
            </div>
            {!regPayResult ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Importo versato €</Label>
                    <Input data-testid="regpay-importo" type="number" step="0.01" value={regPayImporto} onChange={(e) => setRegPayImporto(e.target.value)} className="h-11 text-lg font-bold" />
                  </div>
                  <div>
                    <Label>Metodo di pagamento</Label>
                    <Select value={regPayMetodo} onValueChange={setRegPayMetodo}>
                      <SelectTrigger data-testid="regpay-metodo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bonifico">Bonifico</SelectItem>
                        <SelectItem value="riba_rid">RIBA / RID</SelectItem>
                        <SelectItem value="contanti">Contanti</SelectItem>
                        <SelectItem value="assegno">Assegno</SelectItem>
                        <SelectItem value="pos">Carta / POS</SelectItem>
                        <SelectItem value="altro">Altro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Data pagamento</Label>
                    <Input type="date" value={regPayDate} onChange={(e) => setRegPayDate(e.target.value)} data-testid="regpay-date" />
                  </div>
                  <div>
                    <Label>Note / Riferimento</Label>
                    <Input value={regPayNote} onChange={(e) => setRegPayNote(e.target.value)} data-testid="regpay-note" />
                  </div>
                </div>
                <Button data-testid="regpay-apply" onClick={applyRegPay} disabled={bulkActionBusy || !regPayImporto} className="w-full h-11">
                  {bulkActionBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                  Registra e distribuisci
                </Button>
              </>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 rounded border bg-emerald-50"><div className="text-[10px] uppercase text-emerald-700 font-bold">Pagate</div><div className="text-2xl font-extrabold text-emerald-700">{regPayResult.pagate}</div></div>
                  <div className="p-2 rounded border bg-amber-50"><div className="text-[10px] uppercase text-amber-700 font-bold">Parziali</div><div className="text-2xl font-extrabold text-amber-700">{regPayResult.parziali}</div></div>
                  <div className="p-2 rounded border bg-red-50"><div className="text-[10px] uppercase text-red-700 font-bold">Non pagate</div><div className="text-2xl font-extrabold text-red-700">{regPayResult.non_pagate}</div></div>
                </div>
                {regPayResult.eccedenza > 0 && (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    Eccedenza di <b>{fmt(regPayResult.eccedenza)}</b> non allocata (importo versato superiore al totale dovuto).
                  </div>
                )}
                <div className="border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-secondary"><tr className="text-left">
                      <th className="px-2 py-1.5">N°</th><th className="px-2 py-1.5">Data</th>
                      <th className="px-2 py-1.5 text-right">Allocato</th>
                      <th className="px-2 py-1.5 text-right">Saldo residuo</th>
                      <th className="px-2 py-1.5">Stato</th>
                    </tr></thead>
                    <tbody>
                      {(regPayResult.dettaglio || []).map((d, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="px-2 py-1.5">{d.numero || "—"}</td>
                          <td className="px-2 py-1.5">{d.data}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{fmt(d.allocato)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{fmt(d.saldo_residuo)}</td>
                          <td className="px-2 py-1.5"><Badge className={`text-[9px] ${d.stato_finale === "pagata" ? "bg-emerald-100 text-emerald-800" : d.stato_finale === "parziale" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}>{d.stato_finale}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Button variant="outline" className="w-full" onClick={() => { setRegPayOpen(false); setRegPayResult(null); clearSelection(); }}>Chiudi</Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modale Bulk Status Change */}
      <Dialog open={bulkStatusOpen} onOpenChange={setBulkStatusOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              Cambia Stato Pagamento ({selectedIds.length})
            </DialogTitle>
            <DialogDescription>Aggiorna lo stato di pagamento per le fatture selezionate.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nuovo stato</Label>
              <Select value={bulkStatusValue} onValueChange={setBulkStatusValue}>
                <SelectTrigger data-testid="bulk-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pagata">Pagata</SelectItem>
                  <SelectItem value="da_pagare">Da pagare</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {bulkStatusValue === "pagata" && (
              <>
                <div>
                  <Label>Data pagamento</Label>
                  <Input type="date" value={bulkStatusDate} onChange={(e) => setBulkStatusDate(e.target.value)} data-testid="bulk-status-date" />
                </div>
                <div>
                  <Label>Mezzo di pagamento</Label>
                  <Select value={bulkStatusMezzo} onValueChange={setBulkStatusMezzo}>
                    <SelectTrigger data-testid="bulk-status-mezzo"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MEZZI_PAGAMENTO.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <Button data-testid="bulk-status-apply" onClick={applyBulkStatus} disabled={bulkActionBusy} className="w-full h-11">
              {bulkActionBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Applica a {selectedIds.length} fatture
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {dupImporti && <ImportDoppioniDialog list={dupImporti.list} imported={dupImporti.imported} onClose={() => setDupImporti(null)} onDone={reloadFatture} />}

      {/* Modale Risoluzione Conflitti Dedup (dopo bulk-import con duplicati sospetti) */}
      <Dialog open={conflictOpen} onOpenChange={setConflictOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              {conflictList.length} duplicati sospetti · scegli come procedere
            </DialogTitle>
            <DialogDescription>Risolvi ogni conflitto scegliendo tra sovrascrivere un record esistente, saltare la riga o inserire comunque una nuova fattura.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground bg-amber-50 dark:bg-amber-900/20 p-2 rounded border border-amber-200">
              Per ogni riga: <b>Salta</b> (default, non importa) · <b>Sovrascrivi</b> (aggiorna l'esistente con i nuovi dati) · <b>Tienile entrambe</b> (crea comunque un nuovo record). Evita il doppio conteggio IVA.
            </div>
            {conflictList.map((c, idx) => {
              const nd = c.new_doc || {};
              const dec = c.decision || { action: "skip" };
              return (
                <div key={idx} className="border rounded-lg p-3 bg-card" data-testid={`conflict-row-${idx}`}>
                  <div className="text-xs font-semibold text-primary mb-2">Riga {c.row_index} · Nuova fattura</div>
                  <div className="grid grid-cols-4 gap-2 text-xs mb-3 bg-primary/5 p-2 rounded">
                    <div><span className="text-muted-foreground">N°:</span> <b>{nd.numero || "—"}</b></div>
                    <div><span className="text-muted-foreground">Data:</span> <b>{nd.data || "—"}</b></div>
                    <div><span className="text-muted-foreground">Totale:</span> <b>{fmt(nd.totale || 0)}</b></div>
                    <div><span className="text-muted-foreground">Controparte:</span> <b className="truncate">{nd.controparte || "—"}</b></div>
                  </div>
                  <div className="text-xs text-muted-foreground mb-1">Possibili duplicati esistenti ({(c.existing_matches || []).length}):</div>
                  <div className="space-y-1 mb-3">
                    {(c.existing_matches || []).map((m) => (
                      <label key={m.id} className={`flex items-center gap-2 text-xs p-2 rounded border cursor-pointer ${dec.action === "overwrite" && dec.target_id === m.id ? "bg-emerald-50 border-emerald-400" : "bg-secondary/40 border-border hover:bg-secondary"}`}>
                        <input type="radio" name={`conflict-${idx}`} checked={dec.action === "overwrite" && dec.target_id === m.id}
                               onChange={() => setConflictDecision(idx, "overwrite", m.id)}
                               data-testid={`conflict-${idx}-overwrite-${m.id}`}
                               className="accent-emerald-600" />
                        <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">{m.reason} · {Math.round(m.confidence * 100)}%</span>
                        <span>N° <b>{m.numero || "—"}</b> · {m.data} · {fmt(m.totale)} · {m.controparte || "—"}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex gap-2 text-xs">
                    <label className={`flex items-center gap-1 px-2 py-1 rounded border cursor-pointer ${dec.action === "skip" ? "bg-muted border-foreground/30" : "border-border"}`}>
                      <input type="radio" name={`conflict-${idx}`} checked={dec.action === "skip"}
                             onChange={() => setConflictDecision(idx, "skip", null)}
                             data-testid={`conflict-${idx}-skip`} />
                      Salta (non importare)
                    </label>
                    <label className={`flex items-center gap-1 px-2 py-1 rounded border cursor-pointer ${dec.action === "keep_both" ? "bg-blue-50 border-blue-400" : "border-border"}`}>
                      <input type="radio" name={`conflict-${idx}`} checked={dec.action === "keep_both"}
                             onChange={() => setConflictDecision(idx, "keep_both", null)}
                             data-testid={`conflict-${idx}-keep-both`} />
                      Tienile entrambe (crea nuova)
                    </label>
                  </div>
                </div>
              );
            })}
            <div className="flex gap-2 pt-2 sticky bottom-0 bg-background pb-1">
              <Button variant="outline" className="flex-1" onClick={() => {
                setConflictList((lst) => lst.map((c) => ({ ...c, decision: { action: "skip", target_id: null } })));
              }} data-testid="conflict-skip-all">Salta tutto</Button>
              <Button className="flex-1" onClick={applyConflictResolutions} disabled={bulkActionBusy} data-testid="conflict-apply">
                {bulkActionBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Applica decisioni
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modale conferma tipo documento (dopo OCR foto/PDF) */}
      <Dialog open={!!ocrPreview} onOpenChange={(o) => !o && setOcrPreview(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" /> Che documento è?
            </DialogTitle>
          </DialogHeader>
          {ocrPreview && (
            <div className="space-y-3">
              <div className="bg-secondary rounded-lg p-3 text-sm space-y-1">
                <div className="text-xs text-muted-foreground">L'AI ha riconosciuto:</div>
                <div className="font-bold text-lg flex items-center gap-2">
                  {ocrPreview.tipo_rilevato === "fattura"
                    ? <><Receipt className="h-5 w-5 text-primary" /> Fattura</>
                    : <><Truck className="h-5 w-5 text-primary" /> DDT (bolla)</>}
                  <span className="text-xs font-normal text-muted-foreground">({ocrPreview.confidenza || 0}% confidenza)</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {ocrPreview.tipo_rilevato === "fattura"
                    ? `Fornitore: ${ocrPreview.fattura?.fornitore || "—"} · Numero: ${ocrPreview.fattura?.numero || "—"} · Totale: ${fmt(ocrPreview.fattura?.totale || 0)}`
                    : `Numero: ${ocrPreview.ddt?.numero || "—"} · ${(ocrPreview.ddt?.descrizione || "").slice(0, 60)}`}
                </div>
                {ocrPreview.tipo_rilevato === "fattura" && <OcrBadges o={ocrPreview.fattura} />}
                {ocrBatch.length > 0 && (
                  <div className="text-[10px] text-primary mt-2 font-semibold">Ancora {ocrBatch.length} documento{ocrBatch.length > 1 ? "i" : ""} in coda</div>
                )}
              </div>
              <div className="text-xs text-muted-foreground">Conferma o cambia il tipo:</div>
              <div className="rounded-md border border-border p-2">
                <div className="text-[11px] text-muted-foreground mb-1">Se è un DDT, indica subito il pagamento (per la fattura lo scegli nel modulo successivo):</div>
                <DdtPagamentoFields value={ocrPag} onChange={setOcrPag} testid="ocr-ddt" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="ocr-confirm-fattura" variant={ocrPreview.tipo_rilevato === "fattura" ? "default" : "outline"} onClick={() => confirmOcrType("fattura")}>
                  <Receipt className="h-4 w-4 mr-2" /> È una Fattura
                </Button>
                <Button data-testid="ocr-confirm-ddt" variant={ocrPreview.tipo_rilevato === "ddt" ? "default" : "outline"} onClick={() => confirmOcrType("ddt")}>
                  <FileIcon className="h-4 w-4 mr-2" /> È un DDT
                </Button>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setOcrPreview(null); setOcrBatch([]); }} className="w-full">Annulla tutto</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
